/**
 * Day-wise Rates Utility Functions
 */

/**
 * Generate array of dates between checkin and checkout (exclusive of checkout)
 * @param {number} checkinTimestamp - Unix timestamp in seconds
 * @param {number} checkoutTimestamp - Unix timestamp in seconds
 * @returns {Array<{timestamp: number, label: string, shortLabel: string}>}
 */
export const generateDateRange = (checkinTimestamp, checkoutTimestamp) => {
  const dates = [];
  const oneDay = 86400;
  let current = checkinTimestamp;

  while (current < checkoutTimestamp) {
    const date = new Date(current * 1000);
    dates.push({
      timestamp: current,
      label: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      shortLabel: date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
    });
    current += oneDay;
  }

  return dates;
};

/**
 * Check if any room has existing day_wise_details
 * @param {Array} rooms - Array of room objects
 * @returns {boolean}
 */
export const hasAnyExistingDayWiseDetails = (rooms) => {
  return (rooms || []).some(
    (room) => room.day_wise_details && room.day_wise_details.length > 0
  );
};

/**
 * Count total number of day slots across all confirmed rooms
 * @param {Array} rooms - Array of confirmed room objects
 * @returns {number}
 */
export const countTotalDaySlots = (rooms) => {
  return (rooms || []).reduce((total, room) => {
    const dates = generateDateRange(room.checkin_date, room.checkout_date);
    return total + dates.length;
  }, 0);
};

/**
 * Initialize day-wise details for a room
 * Loads existing day_wise_details if present, or uses split amount for new entries
 * @param {Object} room - Room object from booking
 * @param {number|null} splitAmount - Amount to use for each day if no existing data (null = leave empty)
 * @returns {Array} - Array of day-wise detail objects
 */
export const initializeDayWiseDetails = (room, splitAmount = null) => {
  const dates = generateDateRange(room.checkin_date, room.checkout_date);
  const existingDetails = room.day_wise_details || [];

  return dates.map((dateObj) => {
    const existing = existingDetails.find((d) => d.date === dateObj.timestamp);

    // If existing data found, use it
    if (existing) {
      return {
        date: dateObj.timestamp,
        dateLabel: dateObj.label,
        shortLabel: dateObj.shortLabel,
        amount: existing.amount != null ? existing.amount : "",
        extra_bed_price: existing.extra_bed_price != null ? existing.extra_bed_price : "",
        meal_plan_price: existing.meal_plan_price != null ? existing.meal_plan_price : "",
      };
    }

    // No existing data - use split amount if provided, otherwise empty
    return {
      date: dateObj.timestamp,
      dateLabel: dateObj.label,
      shortLabel: dateObj.shortLabel,
      amount: splitAmount != null ? splitAmount : "",
      extra_bed_price: "",
      meal_plan_price: "",
    };
  });
};

/**
 * Initialize all rooms data with auto-split logic
 * If no room has day_wise_details, split bookingTotal across all day slots
 * If any room has day_wise_details, just load existing data
 * @param {Array} confirmedRooms - Array of confirmed room objects
 * @param {number} bookingTotal - Total booking amount
 * @returns {Object} - Object with room IDs as keys and day-wise details as values
 */
export const initializeAllRoomsData = (confirmedRooms, bookingTotal) => {
  const hasExisting = hasAnyExistingDayWiseDetails(confirmedRooms);
  
  let splitAmount = null;
  
  // If no existing day_wise_details, calculate split amount
  if (!hasExisting && bookingTotal > 0) {
    const totalSlots = countTotalDaySlots(confirmedRooms);
    if (totalSlots > 0) {
      // Round to 2 decimal places
      splitAmount = Math.round((bookingTotal / totalSlots) * 100) / 100;
    }
  }

  const initialData = {};
  confirmedRooms.forEach((room) => {
    initialData[room._id] = initializeDayWiseDetails(room, splitAmount);
  });

  return initialData;
};


/**
 * Filter rooms to only include confirmed status
 * @param {Array} rooms - Array of room objects
 * @returns {Array} - Filtered array of confirmed rooms
 */
export const getConfirmedRooms = (rooms) => {
  return (rooms || []).filter((room) => room.status === "confirmed");
};

/**
 * Calculate total from day-wise details
 * @param {Object} roomsData - Object with room IDs as keys and day-wise details as values
 * @returns {number} - Total amount
 */
export const calculateDayWiseTotal = (roomsData) => {
  return Object.values(roomsData).reduce((total, details) => {
    return (
      total +
      details.reduce((sum, detail) => {
        const amount = detail.amount === "" ? 0 : Number(detail.amount);
        const extraBed = detail.extra_bed_price === "" ? 0 : Number(detail.extra_bed_price);
        const mealPlan = detail.meal_plan_price === "" ? 0 : Number(detail.meal_plan_price);
        return sum + amount + extraBed + mealPlan;
      }, 0)
    );
  }, 0);
};

/**
 * Validate all room rates are filled
 * @param {Object} roomsData - Object with room IDs as keys and day-wise details as values
 * @returns {boolean} - True if all room rates are filled
 */
export const validateRoomRates = (roomsData) => {
  for (const details of Object.values(roomsData)) {
    for (const detail of details) {
      if (detail.amount === "" || detail.amount === null) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Build payload for API
 * @param {Object} params - Parameters
 * @param {string} params.bookingId - Booking ID
 * @param {Object} params.roomsData - Rooms data
 * @param {boolean} params.overrideTotal - Whether to override booking total
 * @param {number} params.dayWiseTotal - Day-wise total
 * @param {number} params.bookingTotal - Current booking total
 * @returns {Object} - Payload for API
 */
export const buildPayload = ({
  bookingId,
  roomsData,
  overrideTotal,
  dayWiseTotal,
  bookingTotal,
}) => {
  return {
    booking_id: bookingId,
    rooms: Object.entries(roomsData).map(([roomId, details]) => ({
      room_id: roomId,
      day_wise_details: details.map((d) => ({
        date: d.date,
        amount: d.amount === "" ? 0 : Number(d.amount),
        extra_bed_price: d.extra_bed_price === "" ? 0 : Number(d.extra_bed_price),
        meal_plan_price: d.meal_plan_price === "" ? 0 : Number(d.meal_plan_price),
      })),
    })),
    overrideBookingTotal: overrideTotal,
    newTotal: overrideTotal ? dayWiseTotal : bookingTotal,
  };
};